import { useState, useMemo, useEffect, useCallback } from 'react';
import { summarizeNumber } from '../services/formatting.service';
import { useNodeData } from '../hooks/useNodeData';
import { usePipelineNode } from '../hooks/usePipelineNode';
import { usePipelineNodeRelationships } from '../hooks/usePipelineNodeRelationships';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Loader2, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Search, AlertCircle, Download } from 'lucide-react';
import ApiService, { ApiError } from '../services/api.service';
import BackgroundService from '../services/bg.service';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

export interface NodeDataTableProps {
  pipelineNodeId: string;
  search?: string;
  defaultSort?: string;
}

export function NodeDataTable({ pipelineNodeId, search: initialSearch = '', defaultSort = '' }: NodeDataTableProps) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState(initialSearch); // Local input value
  const [searchQuery, setSearchQuery] = useState(initialSearch); // Actual search query sent to API
  const [sort, setSort] = useState(defaultSort);
  const [limit] = useState(25);

  // Reset all state when the node changes
  useEffect(() => {
    setPage(1);
    setSearchInput(initialSearch);
    setSearchQuery(initialSearch);
    setSort(defaultSort);
  }, [pipelineNodeId]);

  // Update search input when prop changes
  useEffect(() => {
    setSearchInput(initialSearch);
    setSearchQuery(initialSearch);
  }, [initialSearch]);

  // Download state
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'generating' | 'ready' | 'error'>('idle');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    setDownloadStatus('generating');
    setDownloadUrl(null);
    setDownloadError(null);
    setDownloadDialogOpen(true);

    try {
      const result = await ApiService.getInstance().request(
        'POST',
        `/pipelinenodes/${pipelineNodeId}/download`,
        {
          searchText: searchQuery,
          sort,
        }
      ) as { ok: boolean; job_id: string };

      if (!result.ok || !result.job_id) {
        throw new Error('Failed to start download');
      }

      const jobResult = await BackgroundService.getInstance().waitForJob(result.job_id);

      if (jobResult?.url) {
        setDownloadUrl(jobResult.url);
        setDownloadStatus('ready');
      } else {
        throw new Error('No download URL returned');
      }
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError(error instanceof Error ? error.message : 'Download failed');
      setDownloadStatus('error');
    }
  }, [pipelineNodeId, searchQuery, sort]);

  // Get the current version of the node (not working copy) for field ordering
  const { data: node } = usePipelineNode(pipelineNodeId);
  const { data: relationships } = usePipelineNodeRelationships();
  
  // Fetch the data
  // NOTE: If useNodeData supports keepPreviousData / placeholderData, enable it
  // there instead of manually caching displayData in component state.
  const { data, isLoading, isFetching, error } = useNodeData({
    nodeId: pipelineNodeId,
    page,
    limit,
    search: searchQuery,
    sort,
  });

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPage(1); // Reset to first page when search changes
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Get field order from the current version of the node
  const fieldOrder = useMemo(() => {
    if (!node?.fields) return [];
    return node.fields.map(f => f.name || f.label || '').filter(Boolean);
  }, [node?.fields]);

  // Helper function to check if a column is a foreign key column
  const isForeignKeyColumn = (columnName: string): boolean => {
    return columnName.toUpperCase().startsWith('_PLB_FK_');
  };

  // Helper function to check if a column should be skipped
  const shouldSkipColumn = (columnName: string): boolean => {
    const upper = columnName.toUpperCase();
    return upper.startsWith('_PLB_') && !upper.startsWith('_PLB_FK_');
  };

  // Build a map of FK column name -> friendly display name from relationships
  const fkDisplayNames = useMemo(() => {
    const map: Record<string, string> = {};
    if (relationships) {
      for (const rel of relationships) {
        if (rel.child_foreign_key_name) {
          const key = rel.child_foreign_key_name.toUpperCase();
          map[key] = `FK: ${rel.name || rel.child_foreign_key_name}`;
        }
      }
    }
    return map;
  }, [relationships]);

  // Get all column names from records (union of field order and any additional columns in data)
  // Use data directly — no stale displayData cache
  const allColumns = useMemo(() => {
    const columnsSet = new Set<string>();
    const fkColumns = new Set<string>();

    // First add fields in order (excluding _PLB_ columns)
    fieldOrder.forEach(col => {
      if (isForeignKeyColumn(col)) {
        fkColumns.add(col);
      } else if (!shouldSkipColumn(col)) {
        columnsSet.add(col);
      }
    });

    // Then add any additional columns from records (excluding _PLB_ columns)
    data?.records.forEach(record => {
      Object.keys(record).forEach(key => {
        if (isForeignKeyColumn(key)) {
          fkColumns.add(key);
        } else if (!shouldSkipColumn(key) && !columnsSet.has(key)) {
          columnsSet.add(key);
        }
      });
    });

    // Return in field order, then any extras, then FK columns at the end
    const ordered = fieldOrder.filter(col => !shouldSkipColumn(col) && !isForeignKeyColumn(col) && columnsSet.has(col));
    const extras = Array.from(columnsSet).filter(col => !fieldOrder.includes(col));
    const fkList = Array.from(fkColumns);
    return [...ordered, ...extras, ...fkList];
  }, [fieldOrder, data?.records]);

  const handleSort = (column: string) => {
    if (sort === column) {
      // Toggle to descending
      setSort(`-${column}`);
    } else if (sort === `-${column}`) {
      // Clear sort
      setSort('');
    } else {
      // Set ascending
      setSort(column);
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const getSortIcon = (column: string) => {
    if (sort === column) {
      return <ArrowUp className="h-4 w-4" />;
    } else if (sort === `-${column}`) {
      return <ArrowDown className="h-4 w-4" />;
    }
    return <ArrowUpDown className="h-4 w-4" />;
  };

  // Keep last known pagination values stable during refetches
  const [stablePagination, setStablePagination] = useState({ totalPages: 1, totalRecords: 0 });
  useEffect(() => {
    if (data) {
      setStablePagination({
        totalPages: data.total_pages || 1,
        totalRecords: data.total_records || 0,
      });
    }
  }, [data]);
  const totalPages = data?.total_pages || stablePagination.totalPages;
  const totalRecords = data?.total_records ?? stablePagination.totalRecords;

  // Get error message
  const getErrorMessage = () => {
    if (error instanceof ApiError) {
      // Always prioritize the error message from the API payload if available
      if (error.payload?.msg) {
        return error.payload.msg;
      }
      // Fallback to generic messages only if no specific message is provided
      if (error.code === 404) {
        return 'Data not found. The node may not have any data yet.';
      }
      if (error.code === 400) {
        return 'Invalid request. Please check your search or filter parameters.';
      }
      if (error.code === 422) {
        return 'Invalid request parameters. Please check your input.';
      }
      return `Error loading data (${error.code}). Please try again.`;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'Failed to load data. Please try again.';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search bar and download */}
      <div className="mb-4 flex items-center">
        <div className="flex items-center gap-2">
          <Input
            type="text"
            placeholder="Search data..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-[36rem]"
          />
          <Button
            onClick={handleSearch}
            size="sm"
            disabled={isFetching}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          disabled={isFetching}
        >
          <Download className="h-4 w-4 mr-2" />
          Download CSV
        </Button>
      </div>

      {/* Table container with horizontal scroll or error alert */}
      {error ? (
        <div className="flex-1 overflow-auto border rounded-md flex items-center justify-center p-8">
          <Card className="w-full max-w-md border-destructive">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-destructive mb-1">Error Loading Data</h3>
                  <p className="text-sm text-muted-foreground">
                    {getErrorMessage()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
          
          <div className="flex-1 overflow-auto border rounded-md relative">
          {(isFetching) && (
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-background/50 backdrop-blur-sm z-20">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Loading data...</span>
              </div>
            </div>
          )}
          <div className={isFetching && !data ? 'blur-sm' : ''}>
            <table className="w-full caption-bottom text-sm min-w-full">
              <TableHeader>
                <TableRow>
                  {allColumns.map((column) => (
                    <TableHead
                      key={column}
                      className={`sticky top-0 bg-background z-10 whitespace-nowrap border-b ${isForeignKeyColumn(column) ? 'bg-muted/50' : ''}`}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 font-semibold hover:bg-transparent"
                        onClick={() => handleSort(column)}
                      >
                        <span className="mr-1">
                          {fkDisplayNames[column.toUpperCase()] || column}
                        </span>
                        {getSortIcon(column)}
                      </Button>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.records && data.records.length > 0 ? (
                  data.records.map((record, idx) => (
                    <TableRow key={record._PLB_UUID || idx}>
                      {allColumns.map((column) => {
                        const cellValue = record[column] !== null && record[column] !== undefined
                          ? String(record[column])
                          : '-';
                        return (
                          <TableCell
                            key={column}
                            className={`max-w-[400px] truncate ${isForeignKeyColumn(column) ? 'bg-muted/50' : ''}`}
                            title={cellValue}
                          >
                            {cellValue}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : !isLoading ? (
                  <TableRow>
                    <TableCell colSpan={allColumns.length} className="text-center py-8 text-muted-foreground">
                      No data found
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </table>
          </div>
        </div>
        
      )}

      {/* Pagination */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {data?.records?.length || 0} of {summarizeNumber(totalRecords)} records
          </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || isFetching}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="text-sm text-muted-foreground">
            Page {page} of {summarizeNumber(totalPages)}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isFetching}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Download Dialog */}
      <Dialog
        open={downloadDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDownloadStatus('idle');
            setDownloadUrl(null);
            setDownloadError(null);
          }
          setDownloadDialogOpen(open);
        }}
      >
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Download Data</DialogTitle>
            <DialogDescription>
              {downloadStatus === 'generating' && 'Generating your CSV file...'}
              {downloadStatus === 'ready' && 'Your file is ready to download.'}
              {downloadStatus === 'error' && 'There was an error generating your file.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4 flex flex-col items-center gap-4">
            {downloadStatus === 'generating' && (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  This may take a moment for large datasets...
                </p>
              </div>
            )}
            {downloadStatus === 'ready' && downloadUrl && (
              <Button asChild>
                <a href={downloadUrl} download>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </a>
              </Button>
            )}
            {downloadStatus === 'error' && (
              <div className="text-center">
                <p className="text-sm text-destructive mb-4">
                  {downloadError || 'An unexpected error occurred'}
                </p>
                <Button variant="outline" onClick={() => setDownloadDialogOpen(false)}>
                  Close
                </Button>
              </div>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}